import React, { useState, useMemo, useRef } from 'react';
import { Search, X } from 'lucide-react';

/**
 * Reusable multi-select checkbox list with:
 *   - Search box on top (case-insensitive substring match)
 *   - Viewport that shows ~10 rows and scrolls the rest
 *   - Rows: checkbox + optional icon + name; selected rows highlighted
 *
 * Props:
 *   items:        string[] — list of option names
 *   selected:     string[] — currently-selected values
 *   onToggle:     (name: string) => void — called with the toggled name
 *   maxSelected:  number | null — cap (disables unchecked rows once reached)
 *   icon:         optional react node (e.g. <Tag className="..." />)
 *   iconClassName: className for the per-row icon (auto-injected)
 *   testId:       optional data-testid on the outer container
 *   emptyText:    text to render when items is empty
 *   searchPlaceholder: placeholder for the search input
 */
export default function CheckboxListWithSearch({
  items,
  selected,
  onToggle,
  maxSelected = null,
  Icon = null,
  testId,
  emptyText = 'No options',
  searchPlaceholder = 'Search...',
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    // Normalize a string: lowercase, trim, strip zero-width / non-visible unicode
    // (which can sneak in when tags are copy/pasted) and collapse runs of whitespace.
    const norm = (s) => (s || '')
      .toString()
      .toLowerCase()
      .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '') // zero-width & non-breaking spaces
      .replace(/\s+/g, ' ')
      .trim();

    // Deduplicate case-insensitively so a tag like "Sporting Equipment" doesn't
    // show up twice because both the tags collection and a stall record it slightly differently.
    const seen = new Set();
    const deduped = [];
    for (const n of items || []) {
      const k = norm(n);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      deduped.push(n);
    }

    const q = norm(query);
    if (!q) return deduped;
    return deduped.filter((n) => norm(n).includes(q));
  }, [items, query]);

  return (
    <div data-testid={testId}>
      {/* Search box */}
      <div className="relative mb-2">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          data-testid={testId ? `${testId}-search` : undefined}
          className="w-full pl-9 pr-9 py-2 text-sm rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1a1a1a] text-[#252525] dark:text-white focus:outline-none focus:border-[#FFD12D]"
          style={{ fontSize: '16px' }}
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5 text-gray-400" />
          </button>
        )}
      </div>

      {/* Scrolling checkbox viewport — height ~ 10 rows */}
      <div
        className="bg-white dark:bg-[#1a1a1a] rounded-lg border-2 border-gray-200 dark:border-gray-700 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800"
        style={{ maxHeight: 'calc(10 * 44px)' }}
      >
        {filtered.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 p-4 text-center">
            {query ? `No matches for "${query}"` : emptyText}
          </p>
        ) : (
          filtered.map((name) => {
            const checked = selected.includes(name);
            const disabled = !checked && maxSelected !== null && selected.length >= maxSelected;
            return (
              <label
                key={name}
                className={`flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[#FFD12D]/5 ${checked ? 'bg-[#FFD12D]/10' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => onToggle(name)}
                  className="w-4 h-4 accent-[#FFD12D] flex-shrink-0"
                />
                {Icon && <Icon className="w-4 h-4 text-[#FFD12D] flex-shrink-0" />}
                <span className={`text-sm ${checked ? 'font-semibold text-[#252525] dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                  {name}
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}
